import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import api from "@/lib/api";

const PREF_TOKEN_KEY = "pref_token";
const PREF_USER_KEY = "pref_user";

export default function EngineeringAdmissionLogin() {
  const navigate = useNavigate();
  const [role, setRole] = useState("COUNSELOR");
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const res = await api.post("/preferences/auth/login", {
        identifier,
        password,
        role
      });
      
      const { token, user } = res.data;
      localStorage.setItem(PREF_TOKEN_KEY, token);
      localStorage.setItem(PREF_USER_KEY, JSON.stringify(user));
      
      toast.success(`Welcome, ${user.name}!`);
      
      if (user.role === "ADMIN") {
        navigate("/engineering-admission/admin");
      } else if (user.role === "COUNSELOR") {
        navigate("/engineering-admission/workspace");
      } else {
        navigate("/engineering-admission/my-lists");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed. Please check your credentials.");
    } finally {
      setLoading(false);
    }
  };

  const roles = [
    { key: "COUNSELOR", label: "Counselor", icon: "fa-user-tie", desc: "Email + Password" },
    { key: "ADMIN", label: "Admin", icon: "fa-shield-alt", desc: "Email + Password" },
    { key: "STUDENT", label: "Student", icon: "fa-graduation-cap", desc: "Mobile + Password" },
  ];

  return (
    <>
      <Helmet>
        <title>Login | Engineering Preference System</title>
        <meta name="description" content="Login to the Engineering Admission Preference List Management System." />
      </Helmet>
      
      <style>{`
        .pref-login-page {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #0f172a;
          color: #e2e8f0;
          font-family: 'Inter', sans-serif;
          position: relative;
          overflow: hidden;
        }
        .pref-login-page::before {
          content: '';
          position: absolute;
          top: 0; left: 0; right: 0; bottom: 0;
          background: radial-gradient(ellipse at 30% 20%, rgba(14,165,233,0.08) 0%, transparent 60%),
                      radial-gradient(ellipse at 70% 80%, rgba(245,158,11,0.06) 0%, transparent 60%);
          pointer-events: none;
        }
        .pref-login-card {
          position: relative;
          z-index: 2;
          background: rgba(30, 41, 59, 0.85);
          backdrop-filter: blur(24px);
          padding: 2.5rem;
          border-radius: 24px;
          box-shadow: 0 24px 48px rgba(0,0,0,0.5);
          width: 100%;
          max-width: 440px;
          border: 1px solid rgba(255,255,255,0.06);
        }
        .pref-login-header {
          text-align: center;
          margin-bottom: 2rem;
        }
        .pref-login-logo {
          width: 56px;
          height: 56px;
          background: linear-gradient(135deg, #0ea5e9, #0284c7);
          border-radius: 16px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 1.4rem;
          color: #fff;
          margin-bottom: 1rem;
          box-shadow: 0 8px 24px rgba(14,165,233,0.3);
        }
        .pref-login-header h2 {
          margin: 0;
          font-size: 1.6rem;
          color: #fff;
          font-weight: 800;
        }
        .pref-login-header p {
          margin: 0.4rem 0 0;
          color: #64748b;
          font-size: 0.88rem;
        }
        .pref-role-selector {
          display: flex;
          gap: 8px;
          margin-bottom: 1.75rem;
        }
        .pref-role-btn {
          flex: 1;
          padding: 0.65rem 0.5rem;
          background: rgba(255,255,255,0.03);
          color: #64748b;
          border: 1.5px solid rgba(255,255,255,0.08);
          border-radius: 12px;
          cursor: pointer;
          font-weight: 600;
          font-size: 0.78rem;
          transition: all 0.25s cubic-bezier(0.4,0,0.2,1);
          text-align: center;
          font-family: inherit;
        }
        .pref-role-btn:hover {
          background: rgba(255,255,255,0.06);
          border-color: rgba(255,255,255,0.15);
          color: #cbd5e1;
        }
        .pref-role-btn.active {
          background: rgba(14,165,233,0.12);
          border-color: #0ea5e9;
          color: #38bdf8;
          box-shadow: 0 0 20px rgba(14,165,233,0.15);
        }
        .pref-role-btn i {
          display: block;
          font-size: 1.1rem;
          margin-bottom: 0.3rem;
        }
        .pref-role-btn small {
          display: block;
          font-size: 0.65rem;
          font-weight: 400;
          opacity: 0.7;
          margin-top: 0.15rem;
        }
        .pref-form-group {
          margin-bottom: 1.1rem;
        }
        .pref-form-group label {
          display: block;
          margin-bottom: 0.45rem;
          font-size: 0.82rem;
          color: #94a3b8;
          font-weight: 600;
        }
        .pref-form-input {
          width: 100%;
          padding: 0.7rem 1rem;
          border-radius: 10px;
          background: rgba(0,0,0,0.25);
          border: 1.5px solid rgba(255,255,255,0.08);
          color: #fff;
          outline: none;
          font-size: 0.95rem;
          font-family: inherit;
          box-sizing: border-box;
          transition: border-color 0.2s;
        }
        .pref-form-input:focus {
          border-color: #0ea5e9;
          box-shadow: 0 0 0 3px rgba(14,165,233,0.1);
        }
        .pref-form-input::placeholder {
          color: #475569;
        }
        .pref-login-submit {
          margin-top: 1.25rem;
          width: 100%;
          padding: 0.8rem;
          background: linear-gradient(135deg, #0ea5e9, #0284c7);
          color: #fff;
          border: none;
          border-radius: 12px;
          font-weight: 700;
          font-size: 0.95rem;
          cursor: pointer;
          font-family: inherit;
          box-shadow: 0 6px 20px rgba(14,165,233,0.35);
          transition: all 0.3s;
        }
        .pref-login-submit:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 10px 28px rgba(14,165,233,0.5);
        }
        .pref-login-submit:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        .pref-back-link {
          display: block;
          text-align: center;
          margin-top: 1.5rem;
          background: none;
          border: none;
          color: #475569;
          cursor: pointer;
          font-size: 0.82rem;
          font-family: inherit;
          transition: color 0.2s;
        }
        .pref-back-link:hover { color: #94a3b8; }

        @media (max-width: 480px) {
          .pref-login-card { margin: 1rem; padding: 1.75rem; }
        }
      `}</style>

      <div className="pref-login-page">
        <div className="pref-login-card">
          <div className="pref-login-header">
            <div className="pref-login-logo">
              <i className="fas fa-cogs"></i>
            </div>
            <h2>Preference Portal</h2>
            <p>Engineering Admission Counselling System</p>
          </div>

          <div className="pref-role-selector">
            {roles.map((r) => (
              <button
                key={r.key}
                className={`pref-role-btn ${role === r.key ? 'active' : ''}`}
                onClick={() => { setRole(r.key); setIdentifier(""); }}
              >
                <i className={`fas ${r.icon}`}></i>
                {r.label}
                <small>{r.desc}</small>
              </button>
            ))}
          </div>

          <form onSubmit={handleLogin}>
            <div className="pref-form-group">
              <label>{role === "STUDENT" ? "Mobile Number" : "Email Address"}</label>
              <input 
                className="pref-form-input"
                type={role === "STUDENT" ? "tel" : "email"}
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                required
                placeholder={role === "STUDENT" ? "Enter 10-digit mobile number" : "Enter your email address"}
                autoComplete="username"
              />
            </div>
            
            <div className="pref-form-group">
              <label>Password</label>
              <input 
                className="pref-form-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="••••••••"
                autoComplete="current-password"
              />
            </div>

            <button type="submit" className="pref-login-submit" disabled={loading}>
              {loading ? (
                <><i className="fas fa-spinner fa-spin" style={{marginRight: 8}}></i> Authenticating...</>
              ) : (
                <><i className="fas fa-arrow-right" style={{marginRight: 8}}></i> Login to Portal</>
              )}
            </button>
          </form>
          
          <button className="pref-back-link" onClick={() => navigate('/engineering-admission')}>
            ← Back to Engineering Admissions
          </button>
        </div>
      </div>
    </>
  );
}
